import { connect, } from 'react-redux';
import { useLocation, } from 'react-router-dom';
import React from 'react';
import PropTypes from 'prop-types';
import LockScreen from '../components/lockscreen/LockScreen';
import { hideLockScreen, registerAuthToken, } from '../actions/auth';
import TokenRefreshManager from '../modules/TokenRefreshManager';
import AuthenticationService from '../service/AuthenticationService';
import Environment from '../modules/Environment';

const LockScreenContainer = ({
  isLocked, authentication, location, hideLockScreen: hideScreen,
  registerAuthToken: setAuthToken,
}) => {
  const handleUnlock = async (username, password) => {
    const BASE_URL = Environment.getBaseURL();
    const loginUrl = `${BASE_URL}/login`;

    const response = await fetch(loginUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password, }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Authentication failed', }));
      throw new Error(error.message || 'Invalid credentials');
    }

    const data = await response.json();

    if (!data.isAuthenticated || !data.token) {
      throw new Error('Authentication failed');
    }

    const authData = {
      ...data,
      token: data.accessToken || data.token,
      tenantUserId: data.tenantUserId,
    };

    // Update stored token
    window.localStorage.setItem('PAYIT_TOKEN', JSON.stringify(authData));
    setAuthToken(authData);

    // Restart token refresh manager
    TokenRefreshManager.start();

    // Hide lock screen
    hideScreen();

    // Note: Don't redirect - let the user stay on whatever page they were on
    // The lock screen just overlays whatever page, and removing it reveals the page again
  };

  /**
   * Escape hatch out of the overlay. Unlocking above is a password sign-in against
   * /api/login, and SSO users are never provisioned a password — so for them the
   * form can never succeed. Logging out drops them on the login route, where the
   * auth microsite handles SSO, MFA and passwords alike.
   */
  const handleSignInAgain = async () => {
    try {
      await AuthenticationService.logout();
    } finally {
      // Always lower the overlay: isLocked outlives the logout, and leaving it set
      // would re-cover the dashboard the moment the user signed back in.
      hideScreen();
    }
  };

  // Never cover the login route. The overlay's whole purpose is to preserve a page
  // the user is already working in; over the login form it has nothing to preserve
  // and only asks for the same credentials twice.
  const isLoginRoute = location.pathname === '/';

  if (!isLocked || isLoginRoute) {
    return null;
  }

  // Get display name for lock screen
  let displayName = authentication.email || authentication.username;
  if (!displayName && authentication.token) {
    try {
      const tokenParts = atob(authentication.token).split(':');
      const tokenData = JSON.parse(atob(tokenParts[0]));
      displayName = tokenData.email || tokenData.username || authentication.tenantUserId;
    } catch (error) {
      displayName = authentication.tenantUserId || 'User';
    }
  }

  return (
    <LockScreen
      onUnlock={handleUnlock}
      onSignInAgain={handleSignInAgain}
      userEmail={displayName}
    />
  );
};

LockScreenContainer.propTypes = {
  isLocked: PropTypes.bool,
  authentication: PropTypes.object,
  location: PropTypes.object.isRequired,
  hideLockScreen: PropTypes.func.isRequired,
  registerAuthToken: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  isLocked: state.auth?.isLocked || false,
  authentication: state.auth?.authentication || {},
});

const mapDispatchToProps = {
  hideLockScreen,
  registerAuthToken,
};

const ConnectedLockScreenContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(LockScreenContainer);

// Location hook outermost: connect blocks re-renders on unchanged props, which
// would otherwise swallow route changes.
const LockScreenContainerWithRouter = (props) => {
  const location = useLocation();
  return <ConnectedLockScreenContainer {...props} location={location} />;
};


export default LockScreenContainerWithRouter;
